import {
  DataTable,
  Icon,
  Link,
  Panel,
  useFormContext,
  type DataTableProps,
} from "@validereinc/common-components";
import {
  FormSchemaType,
  type FormSchemaQuestionDisplayType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useState } from "react";
import { CreateFormTemplateAddQuestionDrawer } from "./CreateFormTemplateAddQuestionDrawer";
import styles from "./CreateFormTemplateQuestionPreviewTable.module.scss";
import { DeleteQuestionDialog } from "./DeleteQuestionDialog";
import { useFormTemplateQuestionDrawer } from "./useFormTemplateQuestionDrawer";

const cx = classNames.bind(styles);

export const QUESTION_PREVIEW_TABLE_DESCRIPTION =
  "The answers to these questions will be shown in mobile forms to make submissions easily identifiable.";

export const CreateFormTemplateQuestionPreviewTable = () => {
  const parentForm = useFormContext();

  const [selectedQuestionSectionNumber, setSelectedQuestionSectionNumber] =
    useState<number>(1);
  const formFieldSectionQuestionsName = `config.sections.${selectedQuestionSectionNumber - 1}.questions`;

  const questionPreview = parentForm.watch("question_preview");
  const sections = parentForm.watch(
    "config.sections"
  ) as FormSchemaType["config"]["sections"];
  const questions = parentForm.watch("config.questions");

  const allDisplayQuestions = Object.keys(questions).map(
    (questionId: string) => {
      const sectionIndex = sections.findIndex((section) =>
        section.questions.includes(questionId)
      );
      const sectionNumber = sectionIndex + 1;
      const questionNumber =
        questions[questionId]?.question_number ??
        sections[sectionIndex]?.questions?.findIndex(
          (question) => question === questionId
        ) + 1;

      const questionDetails: FormSchemaQuestionDisplayType = {
        question_id: questionId,
        section_number: sectionNumber ? sectionNumber : undefined,
        question_number: questionNumber ? questionNumber : undefined,
        ...(questions[questionId] ? questions[questionId] : {}),
      };

      return questionDetails;
    }
  );

  const displayQuestions: FormSchemaQuestionDisplayType[] = allDisplayQuestions
    .filter(
      (question) =>
        question.section_number &&
        question?.question_number &&
        questionPreview.some(
          (preview) => preview.question_id === question.question_id
        )
    )
    .sort((a, b) => {
      const sectionNumberA = a.section_number ?? 0;
      const sectionNumberB = b.section_number ?? 0;
      if (sectionNumberA !== sectionNumberB) {
        return sectionNumberA - sectionNumberB;
      }
      return a.question_number - b.question_number;
    });

  const {
    isAddQuestionDrawerOpen,
    questionIdToDelete,
    questionToEdit,
    setQuestionIdToDelete,
    setQuestionToEdit,
    handleDeleteQuestion,
    onCloseAddQuestionDrawer,
    onOpenAddQuestionDrawer,
    onSaveQuestionDrawer,
  } = useFormTemplateQuestionDrawer(
    formFieldSectionQuestionsName,
    displayQuestions
  );

  const onEditQuestion: DataTableProps<FormSchemaQuestionDisplayType>["onRowClick"] =
    (question) => {
      const sectionNumber =
        sections.findIndex((section) =>
          section.questions.includes(question.question_id)
        ) + 1;

      if (!sectionNumber) {
        return;
      }

      setQuestionToEdit(question);
      setSelectedQuestionSectionNumber(sectionNumber);
      onOpenAddQuestionDrawer();
    };

  return (
    <div className={cx("container")}>
      <Panel
        title="Preview Questions"
        className={cx("panel")}
      >
        <p className={cx("panel-description")}>
          {QUESTION_PREVIEW_TABLE_DESCRIPTION}
        </p>
        <DataTable
          headers={[
            {
              key: "question",
              label: "Question",
              renderComponent: ({ item }) => (
                <Link
                  label={item.prompt}
                  onClick={() => onEditQuestion(item)}
                />
              ),
            },
            {
              key: "section",
              label: "Section",
              renderComponent: ({ item }) => (
                <DataTable.DataRow.TextCell value={item.section_number} />
              ),
            },
          ]}
          getItemId={(item) => item.question_id}
          items={displayQuestions}
          emptyStateProps={{
            title: "There are no preview answers",
            suggestion:
              "Mark questions as preview answers in the question details.",
            icon: <Icon variant="clipboard-text" />,
          }}
        />
      </Panel>

      <CreateFormTemplateAddQuestionDrawer
        isOpen={isAddQuestionDrawerOpen}
        onClose={onCloseAddQuestionDrawer}
        onSave={onSaveQuestionDrawer}
        questionToEdit={questionToEdit}
        isDeleteQuestionDialogOpen={!!questionIdToDelete}
        handleDeleteQuestion={() => {
          setQuestionIdToDelete(questionToEdit?.question_id ?? "");
        }}
        sectionNumber={selectedQuestionSectionNumber}
        sectionQuestionsLength={
          sections[selectedQuestionSectionNumber - 1]?.questions.length ?? 0
        }
        allDisplayQuestions={allDisplayQuestions}
      />
      <DeleteQuestionDialog
        isOpen={!!questionIdToDelete}
        onClose={() => {
          setQuestionIdToDelete("");
        }}
        handleDeleteQuestion={() => handleDeleteQuestion(questionIdToDelete)}
        questionIdToDelete={questionIdToDelete}
      />
    </div>
  );
};
